/**
 * Custom hook for asset purchase (capex) modelling
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient, handleApiError } from '@/api/apiClient';
import type {
  AssetPurchaseInput,
  AssetPurchaseListResponse,
} from '@/types/assetPurchase';

interface UseAssetPurchasesResult {
  data: AssetPurchaseListResponse | null;
  loading: boolean;
  error: string | null;
  mutating: boolean;
  mutateError: string | null;
  refresh: () => Promise<void>;
  createAsset: (input: AssetPurchaseInput) => Promise<boolean>;
  updateAsset: (id: string, input: AssetPurchaseInput) => Promise<boolean>;
  deleteAsset: (id: string) => Promise<boolean>;
}

export function useAssetPurchases(): UseAssetPurchasesResult {
  const [data, setData] = useState<AssetPurchaseListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);
  const [mutateError, setMutateError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{ data: AssetPurchaseListResponse }>(
        '/asset-purchases'
      );
      setData(response.data.data);
    } catch (err) {
      try {
        handleApiError(err, 'Failed to load asset purchases');
      } catch (handled) {
        setError(
          handled instanceof Error ? handled.message : 'Failed to load data'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  const mutate = useCallback(
    async (
      fn: () => Promise<unknown>,
      failureMessage: string
    ): Promise<boolean> => {
      try {
        setMutating(true);
        setMutateError(null);
        await fn();
        await fetchData();
        return true;
      } catch (err) {
        try {
          handleApiError(err, failureMessage);
        } catch (handled) {
          setMutateError(
            handled instanceof Error ? handled.message : failureMessage
          );
        }
        return false;
      } finally {
        setMutating(false);
      }
    },
    [fetchData]
  );

  const createAsset = useCallback(
    (input: AssetPurchaseInput) =>
      mutate(
        () => apiClient.post('/asset-purchases', input),
        'Failed to add asset'
      ),
    [mutate]
  );

  const updateAsset = useCallback(
    (id: string, input: AssetPurchaseInput) =>
      mutate(
        () => apiClient.put(`/asset-purchases/${id}`, input),
        'Failed to update asset'
      ),
    [mutate]
  );

  const deleteAsset = useCallback(
    (id: string) =>
      mutate(
        () => apiClient.delete(`/asset-purchases/${id}`),
        'Failed to delete asset'
      ),
    [mutate]
  );

  return {
    data,
    loading,
    error,
    mutating,
    mutateError,
    refresh,
    createAsset,
    updateAsset,
    deleteAsset,
  };
}
